import * as React from "react";

import { cn } from "@/lib/utils";

/** Clean titled card with a small label tab and soft elevation. */
export function ComicPanel({
  title,
  children,
  loud,
  dense,
  className,
}: {
  title: string;
  children: React.ReactNode;
  loud?: string;
  dense?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "comic-shadow relative rounded-xl border border-border bg-card text-card-foreground",
        dense ? "p-3" : "p-5",
        className,
      )}
    >
      <div className="absolute -top-2.5 left-4 rounded-md border border-border bg-background px-2 py-0.5 font-display text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      {loud && (
        <div className="absolute -right-2.5 -top-2.5 rounded-full bg-primary px-3 py-1 font-display text-xs font-semibold uppercase tracking-wide text-primary-foreground shadow-sm">
          {loud}
        </div>
      )}
      <div className="pt-1">{children}</div>
    </div>
  );
}

/** Chat bubble — accent for you, muted surface for the agent, dashed for system. */
export function SpeechBubble({
  from,
  children,
}: {
  from: "you" | "agent" | "system";
  children: React.ReactNode;
}) {
  const mine = from === "you";
  const system = from === "system";
  return (
    <div className={cn("flex", mine ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-md rounded-2xl px-4 py-3 text-sm leading-snug",
          // brand-blue, not `primary`. The token is still the pre-reskin emerald and is also the
          // shared Button fill and the focus ring, so retuning it would reskin the whole app as a
          // side effect of a chat fix. ConversationPanel already renders this way; two primitives
          // disagreeing about the same bubble is the drift the palette rules exist to stop (D-47).
          mine && "border border-brand-blue/25 bg-brand-blue/10 text-foreground",
          !mine && !system && "border border-border bg-muted text-foreground",
          system && "border border-dashed border-border bg-transparent italic text-muted-foreground",
        )}
      >
        <div
          className={cn(
            "mb-1 font-display text-[10px] font-semibold uppercase tracking-wider",
            mine ? "text-brand-blue" : "text-muted-foreground",
          )}
        >
          {mine ? "You" : system ? "System" : "Your agent"}
        </div>
        {children}
      </div>
    </div>
  );
}

/** Stylised neural-net "head" — the agent avatar, themed via tokens. */
export function NeuralHead() {
  const node = "hsl(var(--primary))";
  const link = "hsl(var(--muted-foreground))";
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <circle cx="50" cy="50" r="42" fill="hsl(var(--card))" stroke="hsl(var(--border))" strokeWidth="3" />
      <line x1="30" y1="40" x2="50" y2="50" stroke={link} strokeWidth="1.5" />
      <line x1="70" y1="40" x2="50" y2="50" stroke={link} strokeWidth="1.5" />
      <line x1="30" y1="60" x2="50" y2="50" stroke={link} strokeWidth="1.5" />
      <line x1="70" y1="60" x2="50" y2="50" stroke={link} strokeWidth="1.5" />
      <line x1="30" y1="40" x2="30" y2="60" stroke={link} strokeWidth="1.5" />
      <line x1="70" y1="40" x2="70" y2="60" stroke={link} strokeWidth="1.5" />
      <circle cx="30" cy="40" r="4" fill={node} />
      <circle cx="70" cy="40" r="4" fill={node} />
      <circle cx="30" cy="60" r="4" fill={node} />
      <circle cx="70" cy="60" r="4" fill={node} />
      <circle cx="50" cy="50" r="5" fill={node} />
    </svg>
  );
}
